import { Application } from 'pixi.js';
import { IScene } from './scenes/IScene';
import { MenuScene } from './scenes/MenuScene';
import { GameScene } from './scenes/GameScene';
import { InputState, Quest } from './types';

export class GameApp {
  public app: Application;
  private currentScene: IScene | null = null;
  private highScore: number = 0;

  private input: InputState = {
    up: false,
    down: false,
    left: false,
    right: false,
    action: false,
    sprint: false
  };

  // DOM HUD elements
  private hudElement: HTMLElement | null = null;
  private questsPanel: HTMLElement | null = null;
  private questsList: HTMLElement | null = null;
  private chaosVal: HTMLElement | null = null;
  private chaosBar: HTMLElement | null = null;
  private staminaVal: HTMLElement | null = null;
  private staminaBar: HTMLElement | null = null;
  private timerVal: HTMLElement | null = null;
  private ballChaseBanner: HTMLElement | null = null;
  private ballChaseTimer: HTMLElement | null = null;
  private mobileActionBtn: HTMLElement | null = null;
  private mobileSprintBtn: HTMLElement | null = null;

  constructor() {
    this.app = new Application();
  }

  public async init(container: HTMLElement) {
    await this.app.init({
      background: '#0f172a',
      resizeTo: window,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1
    });

    container.appendChild(this.app.canvas);

    this.initDOM();
    this.initInputs();
    this.initResize();

    // Start with Menu Scene
    this.switchScene('menu');

    // Main game ticker
    this.app.ticker.add((ticker) => {
      if (this.currentScene) {
        this.currentScene.update(ticker.deltaTime);
      }
    });
  }

  private initDOM() {
    this.hudElement = document.getElementById('hud');
    this.questsPanel = document.getElementById('quests-panel');
    this.questsList = document.getElementById('quests-list');
    this.chaosVal = document.getElementById('chaos-val');
    this.chaosBar = document.getElementById('chaos-bar');
    this.staminaVal = document.getElementById('stamina-val');
    this.staminaBar = document.getElementById('stamina-bar');
    this.timerVal = document.getElementById('timer-val');
    this.ballChaseBanner = document.getElementById('ball-chase-banner');
    this.ballChaseTimer = document.getElementById('ball-chase-timer');
    this.mobileActionBtn = document.getElementById('mobile-action-btn');
    this.mobileSprintBtn = document.getElementById('mobile-sprint-btn');

    if (this.mobileActionBtn) {
      this.mobileActionBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.input.action = true;
      });
      this.mobileActionBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        this.input.action = false;
      });
      this.mobileActionBtn.addEventListener('mousedown', () => {
        this.input.action = true;
      });
      this.mobileActionBtn.addEventListener('mouseup', () => {
        this.input.action = false;
      });
    }

    if (this.mobileSprintBtn) {
      this.mobileSprintBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.input.sprint = true;
      });
      this.mobileSprintBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        this.input.sprint = false;
      });
      this.mobileSprintBtn.addEventListener('mousedown', () => {
        this.input.sprint = true;
      });
      this.mobileSprintBtn.addEventListener('mouseup', () => {
        this.input.sprint = false;
      });
    }
  }

  public switchScene(type: 'menu' | 'game') {
    if (this.currentScene) {
      this.app.stage.removeChild(this.currentScene.container);
      this.currentScene.destroy();
      this.currentScene = null;
    }

    if (type === 'menu') {
      if (this.hudElement) this.hudElement.classList.add('hidden');
      if (this.questsPanel) this.questsPanel.classList.add('hidden');
      if (this.ballChaseBanner) this.ballChaseBanner.classList.add('hidden');

      this.currentScene = new MenuScene(this.highScore, () => {
        this.switchScene('game');
      });
    } else if (type === 'game') {
      if (this.hudElement) this.hudElement.classList.remove('hidden');
      if (this.questsPanel) this.questsPanel.classList.remove('hidden');

      const gameScene = new GameScene(
        this.input,
        (finalChaos, isWin) => {
          if (finalChaos > this.highScore) {
            this.highScore = finalChaos;
          }
          if (isWin) {
            this.highScore = 100;
          }
        },
        (chaos, timeLeft, quests, stamina, ballChaseRemaining) => {
          this.updateHUD(chaos, timeLeft, quests, stamina, ballChaseRemaining);
        }
      );

      this.currentScene = gameScene;
      gameScene.start();
    }

    if (this.currentScene) {
      this.app.stage.addChild(this.currentScene.container);
      this.currentScene.resize(this.app.screen.width, this.app.screen.height);
    }
  }

  private updateHUD(
    chaos: number,
    timeLeft: number,
    quests: Quest[],
    stamina: number,
    ballChaseRemaining: number | null
  ) {
    // 1. Chaos Bar
    if (this.chaosVal) this.chaosVal.textContent = `${Math.floor(chaos)}%`;
    if (this.chaosBar) this.chaosBar.style.width = `${Math.min(100, chaos)}%`;

    // 2. Stamina Bar
    if (this.staminaVal) this.staminaVal.textContent = `${Math.floor(stamina)}%`;
    if (this.staminaBar) this.staminaBar.style.width = `${Math.max(0, Math.min(100, stamina))}%`;

    // 3. Timer
    if (this.timerVal) {
      const minutes = Math.floor(timeLeft / 60);
      const seconds = Math.floor(timeLeft % 60);
      this.timerVal.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      if (timeLeft <= 15) {
        this.timerVal.style.color = '#e74c3c';
      } else {
        this.timerVal.style.color = '#ffffff';
      }
    }

    // 4. Ball Chase Banner
    if (this.ballChaseBanner && this.ballChaseTimer) {
      if (ballChaseRemaining !== null && ballChaseRemaining > 0) {
        this.ballChaseBanner.classList.remove('hidden');
        this.ballChaseTimer.textContent = `${ballChaseRemaining.toFixed(1)}s`;
      } else {
        this.ballChaseBanner.classList.add('hidden');
      }
    }

    // 5. Quests List
    if (this.questsList) {
      this.questsList.innerHTML = '';
      quests.forEach(q => {
        const li = document.createElement('li');
        li.className = `quest-item ${q.completed ? 'done' : ''}`;
        li.innerHTML = `
          <span>${q.completed ? '✓' : '•'} ${q.title}</span>
          <span class="quest-count">${q.currentCount}/${q.targetCount}</span>
        `;
        this.questsList?.appendChild(li);
      });
    }
  }

  private initInputs() {
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyW' || e.code === 'ArrowUp') this.input.up = true;
      if (e.code === 'KeyS' || e.code === 'ArrowDown') this.input.down = true;
      if (e.code === 'KeyA' || e.code === 'ArrowLeft') this.input.left = true;
      if (e.code === 'KeyD' || e.code === 'ArrowRight') this.input.right = true;
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') this.input.sprint = true;
      if (e.code === 'Space' || e.code === 'KeyE') {
        this.input.action = true;
        e.preventDefault();
      }
    });

    window.addEventListener('keyup', (e) => {
      if (e.code === 'KeyW' || e.code === 'ArrowUp') this.input.up = false;
      if (e.code === 'KeyS' || e.code === 'ArrowDown') this.input.down = false;
      if (e.code === 'KeyA' || e.code === 'ArrowLeft') this.input.left = false;
      if (e.code === 'KeyD' || e.code === 'ArrowRight') this.input.right = false;
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') this.input.sprint = false;
      if (e.code === 'Space' || e.code === 'KeyE') this.input.action = false;
    });

    // Touch directional controls
    window.addEventListener('touchstart', (e) => {
      if ((e.target as HTMLElement)?.closest('.mobile-btn')) return;

      const touch = e.touches[0];
      const screenW = window.innerWidth;
      const screenH = window.innerHeight;
      const centerX = screenW / 2;
      const centerY = screenH / 2;

      this.input.left = touch.clientX < centerX - 40;
      this.input.right = touch.clientX > centerX + 40;
      this.input.up = touch.clientY < centerY - 40;
      this.input.down = touch.clientY > centerY + 40;
    }, { passive: true });

    window.addEventListener('touchmove', (e) => {
      if ((e.target as HTMLElement)?.closest('.mobile-btn')) return;
      const touch = e.touches[0];
      const screenW = window.innerWidth;
      const screenH = window.innerHeight;
      const centerX = screenW / 2;
      const centerY = screenH / 2;

      this.input.left = touch.clientX < centerX - 40;
      this.input.right = touch.clientX > centerX + 40;
      this.input.up = touch.clientY < centerY - 40;
      this.input.down = touch.clientY > centerY + 40;
    }, { passive: true });

    window.addEventListener('touchend', (e) => {
      if (e.touches.length === 0) {
        this.input.left = false;
        this.input.right = false;
        this.input.up = false;
        this.input.down = false;
      }
    }, { passive: true });
  }

  private initResize() {
    window.addEventListener('resize', () => {
      if (this.currentScene) {
        this.currentScene.resize(this.app.screen.width, this.app.screen.height);
      }
    });
  }
}
